import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const MyProfile = () => {
  const [profile, setProfile] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [selectedGender, setSelectedGender] = useState("");
  const [originalProfile, setOriginalProfile] = useState({});
  const [userId, setUserId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [token , setToken] = useState();

  useEffect(() => {
  const token = localStorage.getItem("STITCHD_ID");
    if (token) {
      setToken(token);
      const decoded = jwtDecode(token);
      const id = decoded._id;
      setUserId(id);
    }
  }, [token]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_URL}/user/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const userData = response.data.user;
        setProfile(userData);
        setOriginalProfile(userData);
        setSelectedGender(userData.gender);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setLoading(false);
      }
    };

    if (token && userId) {
      fetchUserData();
    }
  }, [token, userId]);

  const handleGenderChange = (gender) => {
    if (isEditing) {
      setSelectedGender(gender);
      setProfile((prevProfile) => ({
        ...prevProfile,
        gender: gender,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };

  const handleSave = async () => {
    if (!profile.fullName.trim()) {
      setErrorMessage("Full Name cannot be empty.");
      return;
    }

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/user/update`,

        {
          userId: profile._id,
          fullName: profile.fullName,
          gender: profile.gender,
        },        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 200) {
        setIsEditing(false);
        setOriginalProfile(profile);
        setErrorMessage(""); // Clear error message
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setProfile(originalProfile);
    setSelectedGender(originalProfile.gender);
    setErrorMessage(""); // Clear error message
  };

  return (
    <div className="p-4 max-w-4xl mx-2 md:mx-24 bg-white rounded-lg shadow-md mt-28 border font-twentieth-century">
      <div className="mb-6">
        <div className="flex justify-between">
          <p className="text-3xl font-bold mb-2">My Profile</p>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-red-500 h-8 px-2 border rounded shadow hover:bg-red-500 hover:text-white hover:shadow-xl"
            >
              Edit Profile
            </button>
          )}
        </div>
        <p className="text-gray-400">
          You can edit/update your profile information by clicking the edit
          profile button.
        </p>
      </div>

      <div className="font-twentieth-century">
        <div className="flex flex-wrap mb-4">
          <div className="flex flex-col mb-1 w-full pr-2">
            <label className="font-medium mb-1 text-gray-400">Full Name</label>
            {loading ? (
              <Skeleton width="100%" height={40} />
            ) : (
              <input
                type="text"
                name="fullName"
                value={profile.fullName}
                onChange={handleChange}
                disabled={!isEditing}
                className={`border p-2 rounded ${
                  isEditing ? "" : "bg-gray-200"
                }`}
              />
            )}
            {errorMessage && (
              <p className="text-red-500 mt-1">{errorMessage}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap mb-1">
          <div className="flex flex-col mb-1 w-full md:w-1/2 pr-2">
            <label className="font-medium mb-1 text-gray-400">
              Phone Number
            </label>
            {loading ? (
              <Skeleton width="100%" height={40} />
            ) : (
              <p className="border p-2 rounded bg-gray-200">
                {profile.phoneNumber}
              </p>
            )}
          </div>
          <div className="flex flex-col mb-1 w-full md:w-1/2 md:pl-2">
            <label className="font-medium mb-1 text-gray-400">Email</label>
            {loading ? (
              <Skeleton width="100%" height={40} />
            ) : (
              <p className="border p-2 rounded bg-gray-200">{profile.email}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col mb-4">
          <label className="font-medium mb-1 text-gray-400">Gender</label>
          <div className="flex items-center space-x-4">
            <button
              className={`border p-1 rounded text-xl w-24 ${
                selectedGender === "Male"
                  ? "bg-red-500 text-white"
                  : `${isEditing ? "bg-white" : "bg-gray-200"} text-gray-600`
              }`}
              onClick={() => handleGenderChange("Male")}
              disabled={!isEditing}
            >
              Male
            </button>
            <button
              className={`border p-1 rounded text-xl w-24 ${
                selectedGender === "Female"
                  ? "bg-red-500 text-white"
                  : `${isEditing ? "bg-white" : "bg-gray-200"} text-gray-600`
              }`}
              onClick={() => handleGenderChange("Female")}
              disabled={!isEditing}
            >
              Female
            </button>
            <button
              className={`border p-1 rounded text-xl w-24 ${
                selectedGender === "Other"
                  ? "bg-red-500 text-white"
                  : `${isEditing ? "bg-white" : "bg-gray-200"} text-gray-600`
              }`}
              onClick={() => handleGenderChange("Other")}
              disabled={!isEditing}
            >
              Other
            </button>
          </div>
        </div>
        {isEditing && (
          <div className="flex justify-end space-x-2 mt-10">
            <button
              onClick={handleSave}
              className="bg-red-500 text-white px-4 py-1 rounded shadow hover:bg-red-600 hover:shadow-lg"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="bg-white border text-red-500 px-4 py-1 rounded shadow hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProfile;
